/**
 * Normalize HTTP error bodies (NestJS, class-validator, axios) into user-facing text.
 */

const GENERIC_MESSAGES = new Set([
  'bad request',
  'bad request exception',
  'request failed',
  'internal server error',
  'unauthorized',
  'forbidden',
  'not found',
]);

function isGenericMessage(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (GENERIC_MESSAGES.has(t)) return true;
  if (/^request failed with status code \d+$/i.test(t)) return true;
  return false;
}

function messagesFromValidationItem(item: unknown): string[] {
  if (typeof item === 'string') {
    const s = item.trim();
    return s ? [s] : [];
  }
  if (!item || typeof item !== 'object') return [];
  const obj = item as Record<string, unknown>;
  if (obj.constraints && typeof obj.constraints === 'object') {
    return Object.values(obj.constraints as Record<string, unknown>)
      .filter((x): x is string => typeof x === 'string' && x.trim() !== '')
      .map((x) => x.trim());
  }
  if (Array.isArray(obj.children)) {
    return (obj.children as unknown[]).flatMap(messagesFromValidationItem);
  }
  return [];
}

/**
 * Extract a concrete message from a parsed error response body, or null if nothing useful.
 */
export function normalizeServerErrorPayload(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === 'string') {
    const s = data.trim();
    if (!s || isGenericMessage(s)) return null;
    return s;
  }
  if (typeof data !== 'object') return null;

  const o = data as Record<string, unknown>;

  // ApiErrorFilter uses ResponsePayload.fail(error.message, details) — validation lines are often in `data`
  if (Array.isArray(o.data)) {
    const parts = (o.data as unknown[]).filter((x): x is string => typeof x === 'string' && x.trim() !== '');
    if (parts.length) return [...new Set(parts.map((x) => x.trim()))].join('. ');
  }
  if (typeof o.data === 'string') {
    const ds = o.data.trim();
    if (ds && !isGenericMessage(ds)) return ds;
  }

  // ResponsePayload.fail duplicate mobile: data: { message, existingCustomer }
  if (o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)) {
    const inner = o.data as Record<string, unknown>;
    if (inner.existingCustomer != null && typeof inner.existingCustomer === 'object') {
      const ec = inner.existingCustomer as Record<string, unknown>;
      const fn = typeof ec.firstName === 'string' ? ec.firstName.trim() : '';
      const ln = typeof ec.lastName === 'string' ? ec.lastName.trim() : '';
      const subMsg = typeof inner.message === 'string' ? inner.message.trim() : '';
      const name = [fn, ln].filter(Boolean).join(' ');
      if (subMsg && name) return `${subMsg}. ${name}`;
      if (name) return `Mobile number already registered. ${name}`;
      if (subMsg) return subMsg;
    }
    const nested = normalizeServerErrorPayload(o.data);
    if (nested) return nested;
  }

  if (Array.isArray(o.message)) {
    const parts = (o.message as unknown[]).flatMap(messagesFromValidationItem);
    const unique = [...new Set(parts)];
    if (unique.length) return unique.join('. ');
  }

  if (typeof o.message === 'string') {
    const s = o.message.trim();
    if (!s || isGenericMessage(s)) return null;
    return s;
  }

  if (typeof o.detail === 'string') {
    const s = o.detail.trim();
    if (s && !isGenericMessage(s)) return s;
  }

  if (typeof o.error === 'string') {
    const s = o.error.trim();
    if (s && !isGenericMessage(s)) return s;
  }

  return null;
}

/** Error optionally tagged with duplicate-mobile owner (set by API layer from ResponsePayload). */
export type ErrorWithDuplicateMobile = Error & {
  duplicateMobileOwner?: { firstName: string; lastName: string };
};

export function attachDuplicateMobileFromResponseBody(
  err: Error,
  responseBody: unknown,
): ErrorWithDuplicateMobile {
  const out = err as ErrorWithDuplicateMobile;
  if (!responseBody || typeof responseBody !== 'object') return out;
  const o = responseBody as Record<string, unknown>;
  const inner = o.data;
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return out;
  const d = inner as Record<string, unknown>;
  const ec = d.existingCustomer;
  if (!ec || typeof ec !== 'object') return out;
  const c = ec as Record<string, unknown>;
  const fn = typeof c.firstName === 'string' ? c.firstName.trim() : '';
  const ln = typeof c.lastName === 'string' ? c.lastName.trim() : '';
  if (fn || ln) {
    out.duplicateMobileOwner = { firstName: fn, lastName: ln };
  }
  return out;
}

/** Error optionally tagged with PDR stock validation rows from ResponsePayload.fail data.faulty. */
export type ErrorWithFaultyStocks = Error & {
  faultyStocks?: Record<string, unknown>[];
};

function extractFaultyStocksFromResponseBody(responseBody: unknown): Record<string, unknown>[] | null {
  if (!responseBody || typeof responseBody !== 'object') return null;
  const o = responseBody as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const faulty = (inner as Record<string, unknown>).faulty;
    if (Array.isArray(faulty) && faulty.length > 0) {
      return faulty.filter((row): row is Record<string, unknown> => row != null && typeof row === 'object');
    }
  }
  const top = o.faulty;
  if (Array.isArray(top) && top.length > 0) {
    return top.filter((row): row is Record<string, unknown> => row != null && typeof row === 'object');
  }
  return null;
}

export function attachFaultyStocksFromResponseBody(
  err: Error,
  responseBody: unknown,
): ErrorWithFaultyStocks {
  const out = err as ErrorWithFaultyStocks;
  const faulty = extractFaultyStocksFromResponseBody(responseBody);
  if (faulty) {
    out.faultyStocks = faulty;
  }
  return out;
}

/** HTTP status plus the structured body (`{ code, ...details }`) of a failed request. */
export type ErrorWithApiDetails = Error & {
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
};

export function attachApiErrorMetadataFromResponseBody(
  err: Error,
  responseBody: unknown,
  status?: number,
): ErrorWithDuplicateMobile & ErrorWithFaultyStocks & ErrorWithApiDetails {
  const out = attachFaultyStocksFromResponseBody(
    attachDuplicateMobileFromResponseBody(err, responseBody),
    responseBody,
  ) as ErrorWithDuplicateMobile & ErrorWithFaultyStocks & ErrorWithApiDetails;
  if (status) out.status = status;
  if (responseBody && typeof responseBody === 'object') {
    const inner = (responseBody as Record<string, unknown>).data;
    const details =
      inner && typeof inner === 'object' && !Array.isArray(inner)
        ? (inner as Record<string, unknown>)
        : (responseBody as Record<string, unknown>);
    out.details = details;
    if (typeof details.code === 'string') out.code = details.code;
  }
  return out;
}

export function getApiErrorCode(error: unknown): string | undefined {
  return (error as ErrorWithApiDetails | undefined)?.code;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return (error as ErrorWithApiDetails | undefined)?.status;
}

export const DUPLICATE_RECORD_MESSAGE = 'This record conflicts with an existing one. Please try again.';

const DUPLICATE_KEY_TEXT = /E11000|duplicate key/i;

/** Raw MongoDB duplicate-key errors leak collection/index names; never show them to users. */
function sanitizeServerMessage(message: string): string {
  return DUPLICATE_KEY_TEXT.test(message) ? DUPLICATE_RECORD_MESSAGE : message;
}

/**
 * Best-effort message for any thrown API error (axios shape or plain Error after interceptors).
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = (error ?? {}) as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };

  const fromPayload =
    err.response?.data !== undefined ? normalizeServerErrorPayload(err.response.data) : null;

  if (fromPayload) {
    return sanitizeServerMessage(fromPayload);
  }

  if (error instanceof Error && error.message?.trim()) {
    const m = error.message.trim();
    if (!isGenericMessage(m)) {
      return sanitizeServerMessage(m);
    }
  }

  const status = err.response?.status;
  if (status) {
    return `${fallback} (HTTP ${status})`;
  }

  return fallback;
}
